import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MessagesSquare,
  CornerUpLeft,
  SlidersHorizontal,
  Inbox,
  AlertCircle,
  Check,
  X,
} from "lucide-react";
import DatePicker, { registerLocale } from "react-datepicker";
import { pt } from "date-fns/locale/pt";
import toast from "react-hot-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { conversationsApi, POLL_MS } from "../services/conversationsApi";
import { formatAbsolute, formatRelative, formatShortDay, dayKey } from "../utils/datetime";
import { markdownToPlainText } from "../utils/markdown";
import { listFilters, viewerStateFromSearch } from "../utils/conversationFilters";
import { prefetchChatWidgetConfig } from "../hooks/useChatWidgetConfig";
import ConversationStatusDot, {
  CONVERSATION_STATUSES,
  STATUS_KEYS,
  STATUS_FILTER_KEYS,
} from "./ConversationStatusDot";
import ConversationBotTag from "./ConversationBotTag";
import ContactAvatar from "./ContactAvatar";
import { CsatStars } from "./ConversationCsat";
import { COLUMN_HEADER_CLASS, ROW_TAG_CLASS } from "./conversationLayout";
import ConversationDetail from "./ConversationDetail";
import ConversationContactPanel from "./ConversationContactPanel";
import Skeleton from "./Skeleton";
import "react-datepicker/dist/react-datepicker.css";

// O calendario fala a lingua do backoffice. O ingles e o default do
// componente; so o portugues precisa de ser registado.
registerLocale("pt", pt);

// Quantas linhas cada pedido traz. O scroll infinito pede a seguinte quando a
// sentinela do fundo entra na vista.
const PAGE_SIZE = 50;

// O maximo que o backend aceita num pedido (le=200). O poll silencioso
// refresca a janela que o operador ja carregou num pedido so, e para la deste
// tamanho fica com a cauda como esta em vez de a apagar.
const REFRESH_MAX = 200;

// O que fica na lista depois de um tick silencioso, que refresca so a janela do
// topo (`limit` linhas) e nada para la dela.
//
// A ORDEM DO BACKEND PASSOU A MEXER, e este comentario dizia o contrario. Ate ao
// PR #246 a lista vinha por `ORDER BY c.created_at DESC`, que nunca muda: uma
// conversa nova entrava pelo topo, uma que deixasse de bater com o filtro saia,
// mas nenhuma trocava de lugar com outra. Agora vem pela ultima atividade, e uma
// conversa da cauda que recebe uma mensagem salta para dentro da janela do topo.
//
// O que isso parte aqui e so uma coisa, e esta fechada abaixo: a linha que saltou
// fica nas duas metades da juncao, na janela nova E na cauda antiga, com a mesma
// `key` do React. Por isso a cauda que sobrevive e filtrada pelos ids da janela.
//
// As duas regras da juncao continuam as mesmas:
//
//  - menos linhas do que as pedidas quer dizer que o servidor nao tem mais
//    nenhuma, portanto a lista e exatamente esta e a cauda antiga ja nao
//    existe. Sem isto, com o filtro "Abertas", cem linhas carregadas e cinco
//    resolvidas, o servidor devolvia 95 e as cinco ficavam na lista com o
//    ponto verde enquanto o contador do cabecalho dizia 95;
//  - com a janela cheia, a cauda que sobrevive e a que vem DEPOIS da ultima
//    linha da janela, procurada pelo id e nao pelo indice. Cortar `previous`
//    pelo comprimento da janela perdia a linha que uma conversa nova empurrou
//    para alem da fronteira: nao pertencia a nenhum dos dois lados e sumia da
//    lista, e o `hasMore` ficava preso a verdade para sempre. E se a ultima
//    linha da janela nao esta em `previous`, o que `previous` tinha ali saiu
//    do conjunto e nao ha cauda nenhuma por provar.
//
// Devolve tambem `cursorStillValid`, e nao so as linhas, porque e isso que
// decide de quem e o cursor da pagina seguinte. Quando a juncao deita fora o que
// havia e fica so com a janela, a lista acaba onde a janela acabou e o
// `next_cursor` do tick e o que serve; quando a juncao preserva o que estava
// para la da janela, o cursor que vale continua a ser o que ja estava em mao.
//
// E `cursorStillValid` e nao `tailKept` porque as duas coisas nao sao a mesma no
// ramo em que a cauda inteira saltou para dentro da janela: o filtro esvazia-a,
// nao ficou cauda nenhuma, e o cursor antigo continua correto na mesma. Ele
// nomeia uma chave que esta estritamente abaixo da ultima linha da janela,
// portanto "o que vem depois desta chave" continua a ser exatamente o que falta
// ver, mesmo que a linha que lhe deu o nome esteja agora no topo da lista.
const mergeRefreshedWindow = (previous, fresh, limit) => {
  if (fresh.length < limit) return { items: fresh, cursorStillValid: false };
  const boundary = previous.findIndex((c) => c.id === fresh[fresh.length - 1].id);
  if (boundary === -1) return { items: fresh, cursorStillValid: false };
  const inWindow = new Set(fresh.map((c) => c.id));
  return {
    items: [...fresh, ...previous.slice(boundary + 1).filter((c) => !inWindow.has(c.id))],
    cursorStillValid: true,
  };
};

// A preferencia do operador para a coluna do contacto sobrevive a sessao. Sem
// valor guardado, abre nos ecras onde cabe ao lado da transcricao sem a
// estrangular (2xl, 1536px) e fica fechada nos portateis, onde e o operador
// que decide quando a quer ver.
const CONTACT_PANEL_KEY = "conversations.contactPanel";
const readContactPanelPreference = () => {
  const stored = localStorage.getItem(CONTACT_PANEL_KEY);
  if (stored === "open" || stored === "closed") return stored === "open";
  return window.matchMedia("(min-width: 1536px)").matches;
};

// O dia civil local de hoje menos `back` dias, como "2026-09-04".
const dayBack = (back) => {
  const d = new Date();
  d.setDate(d.getDate() - back);
  return dayKey(d);
};

// Os periodos prontos. `range` devolve [de, ate] em dias civis locais, os dois
// inclusivos aqui; a conversao para o limite exclusivo do backend e feita uma
// so vez, no pedido (o listFilters).
const PERIODS = [
  // "Sempre" e o estado sem filtro nenhum, e esta na lista de proposito: sem
  // ele, quem abrisse o calendario ficava sem saida a vista, porque desligar
  // um periodo era premir outra vez o mesmo chip e isso nao se ve.
  { key: "", labelKey: "conversationsPeriodAny", range: () => ["", ""] },
  { key: "today", labelKey: "conversationsToday", range: () => [dayBack(0), dayBack(0)] },
  { key: "yesterday", labelKey: "conversationsYesterday", range: () => [dayBack(1), dayBack(1)] },
  { key: "7d", labelKey: "conversationsPeriodLast7", range: () => [dayBack(6), dayBack(0)] },
  { key: "30d", labelKey: "conversationsPeriodLast30", range: () => [dayBack(29), dayBack(0)] },
];

// Larguras variadas de proposito: oito barras do mesmo tamanho leem-se como um
// padrao as riscas, e um padrao chama a atencao para si em vez de a devolver a
// lista que esta a chegar.
const SKELETON_ROWS = [
  { name: "w-32", preview: "w-11/12" },
  { name: "w-24", preview: "w-2/3" },
  { name: "w-36", preview: "w-4/5" },
  { name: "w-28", preview: "w-1/2" },
  { name: "w-20", preview: "w-3/4" },
  { name: "w-32", preview: "w-5/6" },
  { name: "w-24", preview: "w-3/5" },
  { name: "w-28", preview: "w-2/3" },
];

// As medidas sao as da `row` la em baixo, para que a primeira pagina aterre em
// cima do esqueleto em vez de o empurrar: px-4 py-3, um disco de 32px com 4px
// de margem em cima, uma caixa de 20px para o nome e a hora, 2px, e uma caixa
// de 18px para o preview. Da 64px por linha, tal como a linha carregada.
const SkeletonRow = ({ name, preview }) => (
  <div className="flex items-start gap-3 px-4 py-3 border-l-2 border-transparent">
    <Skeleton variant="custom" width="w-8" height="h-8" rounded="rounded-full" className="mt-1" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2 h-5">
        <Skeleton variant="custom" width={name} height="h-3.5" rounded="rounded" />
        <Skeleton variant="custom" width="w-8" height="h-3" rounded="rounded" />
      </div>
      <div className="flex items-center h-[18px] mt-0.5">
        <Skeleton variant="custom" width={preview} height="h-3" rounded="rounded" />
      </div>
    </div>
  </div>
);

const ListSkeleton = ({ label, rows = SKELETON_ROWS }) => (
  <div role="status" aria-busy="true" aria-label={label} className="divide-y divide-[var(--color-border)]">
    {rows.map((r, i) => (
      <SkeletonRow key={i} {...r} />
    ))}
  </div>
);

// O estado vazio e o de erro partilham a forma: um disco com um glifo, uma
// frase e uma segunda linha. So o glifo e as palavras mudam, para que os dois
// se leiam como a mesma coisa a dizer coisas diferentes.
const ColumnNotice = ({ icon: Icon, title, hint, action }) => (
  <div className="flex flex-col items-center px-6 py-16 text-center">
    <span
      className="flex items-center justify-center w-10 h-10 rounded-full"
      style={{ backgroundColor: "var(--color-surface-raised)" }}
    >
      <Icon size={20} style={{ color: "var(--color-text-secondary)" }} aria-hidden="true" />
    </span>
    <p className="text-sm font-medium mt-3" style={{ color: "var(--color-text-primary)" }}>
      {title}
    </p>
    {hint && (
      <p className="text-[13px] mt-1" style={{ color: "var(--color-text-secondary)" }}>
        {hint}
      </p>
    )}
    {action}
  </div>
);

// As escolhas da seccao "Avaliacao", um so grupo: qualquer, com avaliacao, ou
// uma estrela de 5 a 1.
const RATING_OPTIONS = [5, 4, 3, 2, 1];

// Uma linha de opcao dentro do painel de filtros: a mesma anatomia das opcoes
// do CustomSelect (texto a esquerda, visto a direita), para o painel se ler
// como parte da mesma familia de controlos.
const OptionRow = ({ selected, onClick, children }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    className="w-full flex items-center justify-between gap-2 h-8 px-2 rounded-md text-[13px] text-left transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)] ring-inset"
    style={{
      color: selected ? "var(--color-text-primary)" : "var(--color-text-secondary)",
      fontWeight: selected ? 500 : 400,
    }}
  >
    <span className="truncate">{children}</span>
    {selected && <Check size={14} className="shrink-0" style={{ color: "var(--color-mint)" }} aria-hidden="true" />}
  </button>
);

// Um periodo pronto e um chip que se preme. Sao dois por linha porque quatro
// numa so nao cabem em 19rem com "Ultimos 30 dias" em portugues.
const PeriodChip = ({ selected, onClick, children, keepsCalendarOpen = false }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    // O unico clique dentro do painel que nao fecha o calendario e o do chip
    // que o abre, senao o `mousedown` fechava-o e o `click` a seguir voltava a
    // abri-lo, e o chip nunca chegava a fechar nada.
    {...(keepsCalendarOpen ? { "data-keeps-calendar-open": "true" } : {})}
    className="h-8 px-3 rounded-md text-[13px] font-medium truncate transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)] ring-inset"
    // Preenchido a mint quando escolhido, com o --color-on-mint por cima: o
    // mesmo par do chip de ordenar dos ficheiros e do botao primario, para
    // esta pagina se parecer com as outras. O custo esta escrito no token.
    style={{
      backgroundColor: selected ? "var(--color-mint)" : "var(--color-surface-raised)",
      color: selected ? "var(--color-on-mint)" : "var(--color-text-primary)",
    }}
  >
    {children}
  </button>
);

const FilterSection = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-medium leading-4 mb-2" style={{ color: "var(--color-text-secondary)" }}>
      {title}
    </h3>
    {children}
  </div>
);

const ConversationsWorkspace = () => {
  const { translate, language } = useLanguage();
  const { id: selectedId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  // Os filtros que chegam no URL (as ligacoes da pagina de analise, D16 do
  // plano do CSAT na pagina) leem-se UMA VEZ, para os valores iniciais do
  // estado, e o URL nunca se escreve de volta. Tem de ser assim: o clique numa
  // linha (`navigate(`/conversations/${c.id}`)`) e o fecho do detalhe
  // (`navigate("/conversations")`) largam a query, e este componente sobrevive
  // a troca de rota (as duas rotas desenham o mesmo RoleRoute >
  // ConversationsWorkspace, sem `key`). Filtros lidos do URL a cada render
  // perdiam-se no primeiro clique. Pelo `useState` com funcao, e nao por um
  // efeito ou um useMemo com `[]`, que o lint das dependencias recusa; e pelo
  // useLocation, porque o `location` global e proibido pelo eslint do CRA. Sem
  // parametros no URL, o estado inicial e exatamente o de antes.
  const [fromUrl] = useState(() => viewerStateFromSearch(search));
  const { isPlatformOnlySuperAdmin } = useAuth();
  const { chats, chatsLoaded, loading: chatsLoading, loadChats } = useChat();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(fromUrl.status);
  const [chatId, setChatId] = useState(fromUrl.chatId);
  // `period` e o chip premido; as duas datas sao o que vai no pedido. Um
  // periodo pronto escreve as duas de uma vez; "escolher datas" deixa o
  // calendario escreve-las uma a uma.
  //
  // `periodBy` diz se as datas sao do inicio ("created", o de sempre) ou do
  // fecho ("resolved", como a pagina de analise conta o CSAT). Um periodo que
  // chega no URL fica como datas escolhidas: pelo fecho nas ligacoes do CSAT,
  // pelo inicio na das mensagens de voz.
  const [period, setPeriod] = useState(fromUrl.period);
  const [periodBy, setPeriodBy] = useState(fromUrl.periodBy);
  const [periodFrom, setPeriodFrom] = useState(fromUrl.from);
  const [periodTo, setPeriodTo] = useState(fromUrl.to);
  // A avaliacao: a estrela e o "com avaliacao" sao um so grupo no painel, e o
  // comentario uma caixa a parte. O operador e o "so com o assistente" nao tem
  // controlo no painel: so chegam pela ligacao, e saem pela pastilha.
  const [csatRating, setCsatRating] = useState(fromUrl.csatRating);
  const [csatRated, setCsatRated] = useState(fromUrl.csatRated);
  const [csatCommented, setCsatCommented] = useState(fromUrl.csatCommented);
  const [operator, setOperator] = useState(fromUrl.operator);
  // A voz tambem so chega pela ligacao (D14), e sai pela pastilha.
  const [voice, setVoice] = useState(fromUrl.voice);
  const [assistantOnly, setAssistantOnly] = useState(fromUrl.assistantOnly);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Separado do `period` de proposito. Enquanto foram a mesma coisa, fechar o
  // calendario obrigava a largar as datas escolhidas, e nao havia forma
  // nenhuma de o fechar sem isso.
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [contactPanelOpen, setContactPanelOpen] = useState(readContactPanelPreference);
  // A transcricao publica aqui o que carregou, para a coluna do contacto ler o
  // contacto sem repetir o pedido. useCallback porque entra nas dependencias do
  // `load` da transcricao: uma funcao nova a cada render reiniciava o polling.
  //
  // O id viaja com os dados, e nao ha aqui efeito nenhum a limpar estado: e a
  // comparacao la em baixo que decide, NO MESMO RENDER, se o que esta em mao e
  // desta conversa ou da anterior. Um efeito corria depois do paint e deixava
  // passar um frame ja pintado com o URL novo e o contacto antigo (medido: tres
  // frames, ~28ms, antes desta mudanca).
  const [loaded, setLoaded] = useState(null);
  const handleConversationLoaded = useCallback(
    (data) => setLoaded({ id: selectedId, data }),
    [selectedId]
  );
  // `loaded` da conversa anterior nao conta como resposta desta: enquanto o id
  // nao bater certo, a coluna esta a ler. A transcricao chama o `onLoaded`
  // tambem quando falha, com null, e ai o painel sabe que ja nao vem nada.
  const contactAnswered = Boolean(loaded && loaded.id === selectedId);
  const activeConversation = contactAnswered ? loaded.data : null;
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Por onde continuar a lista, ou null quando ja nao ha continuacao. E o
  // servidor que o diz, na resposta de cada pagina, e e so isto que decide se ha
  // mais para carregar: o `total` e de um instante anterior e nunca soube
  // responder a essa pergunta sozinho. Opaco, devolve-se tal e qual.
  //
  // Duplicado num ref, e escrito pelo `commitCursor` para os dois nunca se
  // separarem. O estado e para desenhar, o ref e para o `loadMore` poder
  // perguntar, DEPOIS do await, se a lista lhe fugiu por baixo. Escrever no ref
  // na altura do `set` e nao no render seguinte e o que torna essa pergunta
  // independente do ciclo de render.
  const [nextCursor, setNextCursor] = useState(null);
  const nextCursorRef = useRef(null);
  const commitCursor = useCallback((value) => {
    nextCursorRef.current = value;
    setNextCursor(value);
  }, []);
  const [failed, setFailed] = useState(false);

  const filtersButtonRef = useRef(null);
  const filtersPanelRef = useRef(null);
  const calendarRef = useRef(null);
  const scrollerRef = useRef(null);
  const sentinelRef = useRef(null);

  // O guarda e obrigatorio: loadChats e recriado a cada render do provider, e um
  // efeito sem ele dispara duas vezes.
  useEffect(() => {
    if (!isPlatformOnlySuperAdmin() && !chatsLoaded && !chatsLoading) loadChats();
  }, [isPlatformOnlySuperAdmin, chatsLoaded, chatsLoading, loadChats]);

  // Um contador de geracao, como o do ChatContext (loadGeneration.current) e nao
  // o `let current = true` do useChatWidgetConfig: aqui o `load` e partilhado
  // por dois efeitos, o de montagem e o do polling, portanto a marca tem de
  // viver fora do corpo de um efeito. Sem ela a resposta lenta de um filtro
  // antigo aterrava por cima da do filtro que o operador acabou de escolher.
  // Tambem invalida um "carregar mais" apanhado a meio por um tick do poll.
  const loadGeneration = useRef(0);

  // As linhas em mao, lidas pelo poll e pelo "carregar mais" sem entrarem nas
  // dependencias do `load`: com `items` la dentro, cada pagina acrescentada
  // recriava o `load`, o efeito de montagem corria outra vez e a lista voltava
  // ao principio. A lista inteira e nao so o comprimento, porque o "carregar
  // mais" precisa dos ids para saber se a pagina trouxe alguma coisa nova. O
  // catch do `load` tambem le so o comprimento, para saber se ha alguma linha
  // no ecra quando uma leitura falha, no lugar de um segundo ref so para isso.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // O estado nos parametros do pedido, pelo listFilters, que diz porque o
  // "Transferidas" pergunta pelo `handed_off` e nao pelo `status`, e porque um
  // filtro desligado nunca vai como `false`.
  const operatorId = operator ? operator.id : null;
  const queryFilters = useCallback(
    () =>
      listFilters({
        chatId,
        status,
        assistantOnly,
        periodBy,
        from: periodFrom,
        to: periodTo,
        csatRating,
        csatRated,
        csatCommented,
        operatorId,
        voice,
      }),
    [chatId, status, assistantOnly, periodBy, periodFrom, periodTo, csatRating, csatRated, csatCommented, operatorId, voice]
  );

  // `silent` distingue o tick do polling da leitura que o utilizador provocou.
  // Uma leitura provocada volta ao principio com a primeira pagina; um tick
  // refresca a janela que ja esta carregada, num pedido so, para as linhas nao
  // voltarem a nascer por baixo de quem ja rolou ate elas.
  const load = useCallback(
    async ({ silent = false } = {}) => {
      loadGeneration.current += 1;
      const myGeneration = loadGeneration.current;
      if (!silent) {
        setLoading(true);
        // O cursor em mao e do conjunto anterior, e uma leitura provocada e quase
        // sempre um filtro novo. Deixa-lo la ate a resposta chegar mantinha o
        // `hasMore` verdadeiro a custa dele, e a sentinela podia trazer uma pagina
        // do conjunto antigo para dentro da lista do novo, com um buraco no meio.
        commitCursor(null);
      }
      const limit = silent
        ? Math.min(REFRESH_MAX, Math.max(PAGE_SIZE, Math.ceil(itemsRef.current.length / PAGE_SIZE) * PAGE_SIZE))
        : PAGE_SIZE;
      try {
        const data = await conversationsApi.list({ ...queryFilters(), limit });
        if (myGeneration !== loadGeneration.current) return;
        // Pelo `itemsRef` e nao pela forma de atualizacao do setItems, porque a
        // juncao decide duas coisas de uma vez, as linhas e o cursor, e a segunda
        // nao cabe la dentro. E a mesma leitura que o `limit` acima ja faz.
        if (silent) {
          const merged = mergeRefreshedWindow(itemsRef.current, data.items, limit);
          setItems(merged.items);
          if (!merged.cursorStillValid) commitCursor(data.next_cursor || null);
        } else {
          // Uma leitura provocada recomeca a paginacao do principio, portanto o
          // cursor que vale e o desta primeira pagina.
          setItems(data.items);
          commitCursor(data.next_cursor || null);
        }
        setTotal(data.total);
        setFailed(false);
        setLoading(false);
        if (!silent && scrollerRef.current) scrollerRef.current.scrollTop = 0;
      } catch (error) {
        console.error("conversations list failed", error);
        if (myGeneration !== loadGeneration.current) return;
        // Uma leitura silenciosa nao substitui a lista que o operador esta a
        // ler, e essa e a razao de todo o `!silent` aqui. Mas quando ainda nao ha
        // linha nenhuma no ecra nao ha nada para proteger, e ficar calado deixa
        // um esqueleto sem mensagem e sem "tentar novamente". Acontece quando a
        // leitura da montagem e ultrapassada por um tick que depois falha.
        const surface = !silent || itemsRef.current.length === 0;
        if (!silent) toast.error(translate("conversationsLoadFailed"));
        if (surface) {
          setFailed(true);
          setLoading(false);
        }
      } finally {
        // O "a carregar" apaga-se em quem tem algo para mostrar, e nao aqui. Este
        // finally exigia `!silent` E ser a leitura corrente ao mesmo tempo, e
        // quando o pedido demora mais do que o POLL_MS as duas condicoes ficam
        // divididas entre duas leituras: a da montagem e ultrapassada por um tick
        // silencioso antes de acabar, portanto falha o `current`, e o tick que a
        // ultrapassa falha o `!silent`. Nenhuma apagava nada e o esqueleto ficava
        // para sempre. Medido no browser: o pedido levou 17s, 27s e 27s em tres
        // leituras seguidas contra um POLL_MS de 10s, com o backend a responder
        // 200 doze vezes.
      }
    },
    [queryFilters, translate, commitCursor]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const hasMore = Boolean(nextCursor);

  // A pagina seguinte, acrescentada ao que ja esta. Um tick do poll que aterre
  // pelo meio muda a geracao e este resultado e deitado fora: o poll ja
  // refrescou a janela e a sentinela volta a pedir se ainda faltar.
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const myGeneration = loadGeneration.current;
    const myCursor = nextCursorRef.current;
    setLoadingMore(true);
    try {
      const data = await conversationsApi.list({
        ...queryFilters(),
        limit: PAGE_SIZE,
        cursor: myCursor,
      });
      // O cursor tambem, e nao so a geracao. A geracao so protege numa direcao:
      // um tick que ARRANQUE depois deste pedido invalida-o, mas um que tenha
      // arrancado antes e aterre antes partilha a geracao e passa. Quando esse
      // tick traz uma janela maior do que a lista em mao (o `limit` do tick
      // arredonda para cima do PAGE_SIZE, portanto isso acontece assim que o
      // comprimento deixa de ser multiplo de 50), a juncao substitui a lista
      // inteira e avanca o cursor, e o que este pedido traz sao linhas que ja la
      // estao. Sem esta segunda prova, cinquenta `key` repetidas no mesmo `divide-y`.
      if (myGeneration !== loadGeneration.current || nextCursorRef.current !== myCursor) return;
      // Sem filtro de repetidas, ao contrario da versao com `offset`. O cursor
      // nomeia a ultima linha em mao e o servidor devolve o que vem ESTRITAMENTE
      // depois dela, portanto uma linha ja vista nao cabe nesta pagina: para
      // voltar a aparecer teria de descer na lista, e a atividade de uma conversa
      // so anda para a frente. O `reachedEnd` que o filtro alimentava tambem
      // desapareceu; quem responde "ha mais?" e o `next_cursor`, que e a pergunta
      // que o `total` nunca soube responder.
      setItems((previous) => [...previous, ...data.items]);
      commitCursor(data.next_cursor || null);
      setTotal(data.total);
    } catch (error) {
      console.error("conversations load more failed", error);
      toast.error(translate("conversationsLoadFailed"));
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, queryFilters, translate, commitCursor]);

  // A sentinela e o ultimo elemento da lista; quando chega a 200px da vista,
  // pede-se a pagina seguinte. O root e o scroller da coluna e nao a janela,
  // porque e ele que rola.
  useEffect(() => {
    const root = scrollerRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, rootMargin: "200px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, hasMore, loading, failed]);

  // Fechar o calendario guarda as datas que ja foram escolhidas. Sem nenhuma,
  // o periodo volta a "Sempre": um chip premido sem filtro nenhum por tras
  // seria uma mentira.
  const closeCalendar = useCallback(() => {
    setCalendarOpen(false);
    setPeriod((current) => (current === "custom" && !periodFrom && !periodTo ? "" : current));
  }, [periodFrom, periodTo]);

  // O painel de filtros fecha ao clicar fora e com Escape, que devolve o foco
  // ao botao, como o CustomSelect ja faz com a sua lista.
  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onPointerDown = (event) => {
      const inPanel = filtersPanelRef.current?.contains(event.target);
      const onButton = filtersButtonRef.current?.contains(event.target);
      if (!inPanel && !onButton) {
        setFiltersOpen(false);
        return;
      }
      // Dentro do painel: o calendario e uma gaveta, e um clique em qualquer
      // outro sitio do painel fecha-a. Fora ficam so o proprio calendario e o
      // chip que o abre, que tem a sua propria alternancia.
      const inCalendar = calendarRef.current?.contains(event.target);
      const onCalendarChip = event.target.closest?.("[data-keeps-calendar-open]");
      if (!inCalendar && !onCalendarChip) closeCalendar();
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      // Uma camada de cada vez: com o calendario aberto, o Escape fecha-o e o
      // painel fica; so o segundo Escape e que fecha o painel.
      if (calendarOpen) {
        closeCalendar();
        return;
      }
      setFiltersOpen(false);
      filtersButtonRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [filtersOpen, calendarOpen, closeCalendar]);

  const toggleContactPanel = useCallback(() => {
    setContactPanelOpen((open) => {
      localStorage.setItem(CONTACT_PANEL_KEY, open ? "closed" : "open");
      return !open;
    });
  }, []);

  const statusLabel = (s) => (STATUS_KEYS[s] ? translate(STATUS_KEYS[s]) : s);

  // Um dos chips esta sempre premido, e "Sempre" e o que nao filtra nada.
  // Qualquer periodo pronto escreve as duas datas de uma vez e fecha o
  // calendario, se estiver aberto.
  const choosePeriod = (key) => {
    setPeriod(key);
    const [from, to] = PERIODS.find((p) => p.key === key)?.range() || ["", ""];
    setPeriodFrom(from);
    setPeriodTo(to);
    setCalendarOpen(false);
  };

  // O chip "Escolher datas" abre e fecha o calendario. Aberto de novo com
  // datas ja escolhidas, e para as corrigir, e por isso nao as apaga.
  const toggleCalendar = () => {
    if (calendarOpen) {
      closeCalendar();
      return;
    }
    setPeriod("custom");
    setCalendarOpen(true);
  };

  // O calendario devolve [inicio, fim]; o fim e null ate ao segundo clique, e
  // nesse intervalo o pedido leva so o limite de baixo, o que ja e um filtro
  // honesto ("desde este dia").
  const chooseCustomRange = ([start, end]) => {
    setPeriodFrom(start ? dayKey(start) : "");
    setPeriodTo(end ? dayKey(end) : "");
    // Com as duas pontas escolhidas o calendario ja nao tem o que perguntar.
    if (start && end) setCalendarOpen(false);
  };

  // Remover o periodo volta tambem a "Iniciadas", para "nada ligado" ser
  // exatamente o de antes.
  const clearPeriod = () => {
    choosePeriod("");
    setPeriodBy("created");
  };

  // Um so grupo: escolher uma estrela tira o "com avaliacao", e o contrario.
  const chooseRating = (rating, rated = false) => {
    setCsatRating(rating);
    setCsatRated(rated);
  };

  const clearFilters = () => {
    setChatId("");
    clearPeriod();
    chooseRating(null);
    setCsatCommented(false);
    setOperator(null);
    setVoice(false);
    setAssistantOnly(false);
  };

  // Um botao de estado tira o "so com o assistente": o "Transferidas" e o
  // contrario dele, e os outros nao se leem junto com ele.
  const chooseStatus = (value) => {
    setStatus(value);
    setAssistantOnly(false);
  };

  // O estado nao conta como filtro "ativo": tem os seus botoes a vista. Este
  // numero e o das pastilhas: o chat, o periodo, a avaliacao, o comentario, o
  // operador, o "so com o assistente" e a voz.
  const activeFilterCount =
    (chatId ? 1 : 0) +
    (periodFrom || periodTo ? 1 : 0) +
    (csatRating || csatRated ? 1 : 0) +
    (csatCommented ? 1 : 0) +
    (operator ? 1 : 0) +
    (voice ? 1 : 0) +
    (assistantOnly ? 1 : 0);
  const anyFilterOn = activeFilterCount > 0 || status !== "";

  // O nome do chat so distingue alguma coisa quando o tenant tem mais do que um.
  const manyChats = chats.length > 1;
  const chatName = (id) => chats.find((c) => c.id === id)?.name || "";
  // E na LINHA so distingue quando a pagina em mao mistura chats: com dois
  // chats configurados e cinquenta conversas todas do mesmo, cinquenta chips
  // "Website" nao dizem nada e roubam espaco ao nome e a hora.
  const mixedChats = manyChats && new Set(items.map((c) => c.chat_id)).size > 1;

  // A palavra do chip de periodo: o nome do periodo pronto, ou as datas
  // escolhidas por extenso curto.
  const periodLabel = () => {
    const preset = PERIODS.find((p) => p.key === period);
    if (preset) return translate(preset.labelKey);
    if (periodFrom && periodTo && periodFrom !== periodTo) {
      return translate("conversationsPeriodRange", {
        from: formatShortDay(periodFrom, language),
        to: formatShortDay(periodTo, language),
      });
    }
    return formatShortDay(periodFrom || periodTo, language);
  };

  const periodChipLabel = () =>
    periodBy === "resolved" ? translate("conversationsChipClosed", { period: periodLabel() }) : periodLabel();

  const activeChips = [
    ...(chatId ? [{ key: "chat", label: chatName(chatId) || translate("conversationsChat"), remove: () => setChatId("") }] : []),
    ...(periodFrom || periodTo ? [{ key: "period", label: periodChipLabel(), remove: clearPeriod }] : []),
    ...(csatRating || csatRated
      ? [
          {
            key: "rating",
            label: csatRating ? translate(`analytics.rating.${csatRating}`) : translate("conversationsCsatRated"),
            remove: () => chooseRating(null),
          },
        ]
      : []),
    ...(csatCommented
      ? [{ key: "commented", label: translate("conversationsCsatCommented"), remove: () => setCsatCommented(false) }]
      : []),
    ...(operator
      ? [
          {
            key: "operator",
            label: operator.name
              ? translate("conversationsChipOperator", { name: operator.name })
              : translate("conversationsChipOperatorUnnamed"),
            remove: () => setOperator(null),
          },
        ]
      : []),
    ...(voice ? [{ key: "voice", label: translate("conversationsChipVoice"), remove: () => setVoice(false) }] : []),
    ...(assistantOnly
      ? [{ key: "assistantOnly", label: translate("conversationsChipAssistantOnly"), remove: () => setAssistantOnly(false) }]
      : []),
  ];

  const row = (c) => {
    const selected = c.id === selectedId;
    const lastActivity = c.last_message_at || c.created_at;
    const anonymous = !c.contact_label;
    // Uma conversa aberta que ja passou a um humano e a que o operador procura;
    // o ponto do canto mostra isso em vez do verde de "aberta".
    const dotStatus = c.handed_off && c.status === "open" ? "handed_off" : c.status;

    return (
      <button
        key={c.id}
        type="button"
        onClick={() => {
          // O tema do chat comeca a carregar aqui e nao la dentro: a linha ja
          // sabe o chat_id, e esperar pela transcricao poe os dois pedidos em
          // serie. Nao ha `await` porque o resultado nao e para este clique, e
          // para o hook que o vai encontrar em cache.
          prefetchChatWidgetConfig(c.chat_id);
          navigate(`/conversations/${c.id}`);
        }}
        onMouseEnter={() => prefetchChatWidgetConfig(c.chat_id)}
        aria-current={selected ? "true" : undefined}
        // Barra e tom mint, como o resto do backoffice marca o que esta
        // selecionado. O mint da 2.00:1 e sozinho nao chegaria para um
        // indicador, mas a linha escolhida nunca esta sozinha a dizer-se: leva
        // `aria-current`, e o painel ao lado mostra a conversa que ela abriu.
        className={`w-full text-left flex items-start gap-3 px-4 py-3 border-l-2 transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)] ring-inset ${
          selected
            ? "border-[var(--color-mint)] bg-[var(--color-mint-muted)]"
            : "border-transparent hover:bg-[var(--color-surface-raised)]"
        }`}
      >
        {/* O disco leva o estado no canto. Um ponto so nas conversas que nao
            estao no estado calmo, com um anel da cor da folha para se ler por
            cima do disco. A palavra vai em sr-only: o estado nunca e so uma
            cor, mas na lista a palavra visivel em todas as linhas era ruido. */}
        <span className="relative shrink-0 mt-1">
          <ContactAvatar seed={c.contact_label || c.contact_id} name={c.contact_label} size={32} />
          <ConversationStatusDot status={dotStatus} corner />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2 h-5">
            <span className="flex items-baseline gap-2 min-w-0">
              {/* "Anonimo" e um lugar-vazio, nao um nome: pintado como nome
                  era uma das razoes de a lista se ler toda igual. */}
              <span
                className={`text-sm truncate ${anonymous ? "" : "font-medium"}`}
                style={{ color: anonymous ? "var(--color-text-secondary)" : "var(--color-text-primary)" }}
              >
                {c.contact_label || translate("conversationsAnonymous")}
              </span>
              {/* O ponto do canto e a palavra tem de dizer o mesmo: numa
                  conversa aberta que ja passou a um humano o ponto e ambar e
                  a palavra tem de o acompanhar, como o cabecalho da
                  transcricao faz com as duas pastilhas. Sao dois factos
                  separados e nao um estado so: o `status` e aberta/resolvida
                  e o `handed_off` e derivado. */}
              <span className="sr-only">
                {statusLabel(c.status)}
                {c.handed_off ? `, ${translate("conversationsStatusHandedOff")}` : ""}
                {c.bot ? `, ${translate("conversationsBot")}` : ""}
              </span>
              {c.csat_rating && (
                <CsatStars rating={c.csat_rating} translate={translate} className="text-xs leading-4 shrink-0" />
              )}
              <ConversationBotTag bot={c.bot} hidden />
              {mixedChats && chatName(c.chat_id) && (
                <span
                  className={`${ROW_TAG_CLASS} leading-4 max-w-[6rem] truncate`}
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  {chatName(c.chat_id)}
                </span>
              )}
            </span>
            <time
              dateTime={lastActivity}
              className="text-xs shrink-0 tabular-nums"
              style={{ color: "var(--color-text-secondary)" }}
              title={formatAbsolute(lastActivity, language)}
            >
              {formatRelative(lastActivity, language)}
            </time>
          </span>

          <span className="flex items-center gap-1.5 h-[18px] mt-0.5">
            {/* O preview e a ultima coisa dita. Quando foi o agente ou o
                operador a dizer, a seta diz-o sem gastar uma palavra do espaco
                da frase: a ultima palavra foi nossa, e o cliente nao esta a
                espera. */}
            {(c.preview_role === "assistant" || c.preview_role === "operator") && (
              <CornerUpLeft
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: "var(--color-text-secondary)" }}
                aria-hidden="true"
              />
            )}
            <span className="text-[13px] leading-[18px] truncate" style={{ color: "var(--color-text-secondary)" }}>
              {c.preview ? markdownToPlainText(c.preview) : translate("conversationsNoMessages")}
            </span>
          </span>
        </span>
      </button>
    );
  };

  const statusOptions = [
    { value: "", label: translate("conversationsAll") },
    ...CONVERSATION_STATUSES.map((s) => ({ value: s, label: translate(STATUS_FILTER_KEYS[s]) })),
  ];

  const retry = (
    <button
      type="button"
      onClick={() => load()}
      className="mt-3 text-[13px] font-medium underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
      style={{ color: "var(--color-text-primary)" }}
    >
      {translate("conversationsRetry")}
    </button>
  );

  const toDate = (yyyyMmDd) => (yyyyMmDd ? new Date(`${yyyyMmDd}T00:00:00`) : null);

  return (
    // Uma folha so, que preenche o que sobra do <main> (que nesta rota e uma
    // coluna flex, ver App.js). As tres colunas partilham a borda, o raio e a
    // altura, e separam-se por regras de 1px; eram tres cartoes com 16px de
    // intervalo e liam-se como tres widgets em vez de uma caixa de entrada.
    // O minimo e o unico caso em que a pagina rola: um ecra mais baixo do que
    // isto nao tem onde por uma transcricao.
    <div className="card flex flex-1 min-h-[28rem] overflow-hidden">
      {/* Abaixo de lg so ha espaco para um painel: a lista em /conversations,
          a transcricao em /conversations/:id. */}
      <section
        aria-label={translate("conversations")}
        aria-busy={loading}
        className={`w-full lg:w-[22rem] shrink-0 flex-col min-h-0 lg:border-r lg:border-[var(--color-border)] ${
          selectedId ? "hidden lg:flex" : "flex"
        }`}
      >
        <header className={`${COLUMN_HEADER_CLASS} relative justify-between gap-3`}>
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-[15px] font-semibold leading-5 truncate" style={{ color: "var(--color-text-primary)" }}>
              {translate("conversations")}
            </h1>
            {!loading && !failed && (
              <span
                className="inline-flex items-center h-5 px-2 rounded-full text-xs font-medium tabular-nums"
                style={{ backgroundColor: "var(--color-surface-raised)", color: "var(--color-text-secondary)" }}
              >
                {/* O numero sozinho e o que se le de relance; a frase inteira
                    fica para quem ouve. Um aria-label num span sem papel nao e
                    garantido que seja lido. */}
                <span aria-hidden="true">{total}</span>
                <span className="sr-only">{translate("conversationsCount", { count: total })}</span>
              </span>
            )}
          </div>
          <button
            ref={filtersButtonRef}
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-haspopup="dialog"
            aria-label={
              activeFilterCount
                ? `${translate("conversationsFilters")}, ${translate("conversationsFiltersActive", { count: activeFilterCount })}`
                : translate("conversationsFilters")
            }
            title={translate("conversationsFilters")}
            className="relative flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
            style={{
              color: filtersOpen || activeFilterCount ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              backgroundColor: filtersOpen ? "var(--color-surface-raised)" : undefined,
            }}
          >
            <SlidersHorizontal size={16} aria-hidden="true" />
            {activeFilterCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[11px] font-semibold leading-none"
                style={{ backgroundColor: "var(--color-mint)", color: "var(--color-on-mint)" }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* O painel de filtros. Ancorado ao botao, dentro da coluna, portanto
              nunca sai da folha: 19rem cabem nos 22rem da coluna. Aplica em
              direto, como os botoes de estado; nao ha "aplicar". */}
          {filtersOpen && (
            <div
              ref={filtersPanelRef}
              role="dialog"
              aria-label={translate("conversationsFilters")}
              className="absolute right-3 top-[calc(100%-4px)] z-20 w-[19rem] max-h-[70vh] overflow-y-auto rounded-xl border border-[var(--color-border)] flex flex-col"
              style={{ backgroundColor: "var(--color-surface)", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
            >
              {/* O cabecalho fica colado no topo: com o calendario aberto o
                  painel rola, e um botao de fechar que sai da vista e um
                  dialogo sem saida. */}
              <div
                className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 h-11 border-b border-[var(--color-border)]"
                style={{ backgroundColor: "var(--color-surface)" }}
              >
                <h2 className="text-[13px] font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {translate("conversationsFilters")}
                </h2>
                <div className="flex items-center gap-1">
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="text-xs font-medium px-2 h-7 rounded-md transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      {translate("conversationsClearFilters")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      closeCalendar();
                      setFiltersOpen(false);
                      filtersButtonRef.current?.focus();
                    }}
                    aria-label={translate("close")}
                    className="flex items-center justify-center w-7 h-7 rounded-md transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
                    style={{ color: "var(--color-text-secondary)" }}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="p-3 flex flex-col gap-4">
              <FilterSection title={translate("conversationsPeriod")}>
                {/* Pelo inicio ou pelo fecho. "Iniciadas" e o de sempre; a
                    pagina de analise liga para aqui com "Fechadas". */}
                <div role="group" aria-label={translate("conversationsPeriodBy")} className="grid grid-cols-2 gap-1.5 mb-2">
                  <PeriodChip selected={periodBy === "created"} onClick={() => setPeriodBy("created")}>
                    {translate("conversationsPeriodByStart")}
                  </PeriodChip>
                  <PeriodChip selected={periodBy === "resolved"} onClick={() => setPeriodBy("resolved")}>
                    {translate("conversationsPeriodByClose")}
                  </PeriodChip>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {PERIODS.map((p) => (
                    <PeriodChip key={p.key || "any"} selected={period === p.key} onClick={() => choosePeriod(p.key)}>
                      {translate(p.labelKey)}
                    </PeriodChip>
                  ))}
                  <PeriodChip
                    selected={period === "custom"}
                    onClick={toggleCalendar}
                    keepsCalendarOpen
                  >
                    {translate("conversationsPeriodCustom")}
                  </PeriodChip>
                </div>
                {calendarOpen && (
                  // O calendario em linha, e nao num popup: um popup dentro de
                  // um painel dentro de uma folha com overflow escondido e um
                  // popup cortado. Em linha ocupa o espaco e pronto.
                  <div ref={calendarRef} className="mt-2 flex justify-center range-calendar">
                    <DatePicker
                      inline
                      selectsRange
                      startDate={toDate(periodFrom)}
                      endDate={toDate(periodTo)}
                      onChange={chooseCustomRange}
                      maxDate={new Date()}
                      locale={language === "pt" ? "pt" : undefined}
                    />
                  </div>
                )}
              </FilterSection>

              {manyChats && (
                <FilterSection title={translate("conversationsChat")}>
                  <div role="radiogroup" aria-label={translate("conversationsChat")} className="-mx-1">
                    <OptionRow selected={chatId === ""} onClick={() => setChatId("")}>
                      {translate("conversationsAllChats")}
                    </OptionRow>
                    {chats.map((c) => (
                      <OptionRow key={c.id} selected={chatId === c.id} onClick={() => setChatId(c.id)}>
                        {c.name}
                      </OptionRow>
                    ))}
                  </div>
                </FilterSection>
              )}

              <FilterSection title={translate("conversationsCsatFilter")}>
                <div role="radiogroup" aria-label={translate("conversationsCsatFilter")} className="-mx-1">
                  <OptionRow selected={!csatRating && !csatRated} onClick={() => chooseRating(null)}>
                    {translate("conversationsCsatAny")}
                  </OptionRow>
                  <OptionRow selected={!csatRating && csatRated} onClick={() => chooseRating(null, true)}>
                    {translate("conversationsCsatRated")}
                  </OptionRow>
                  {RATING_OPTIONS.map((rating) => (
                    <OptionRow key={rating} selected={csatRating === rating} onClick={() => chooseRating(rating)}>
                      {translate(`analytics.rating.${rating}`)}
                    </OptionRow>
                  ))}
                </div>
                <label
                  className="mt-2 flex items-center gap-2 h-8 px-1 text-[13px] cursor-pointer"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  <input
                    type="checkbox"
                    checked={csatCommented}
                    onChange={(event) => setCsatCommented(event.target.checked)}
                    className="w-4 h-4 accent-[var(--color-mint)]"
                  />
                  {translate("conversationsCsatCommented")}
                </label>
              </FilterSection>

              </div>
            </div>
          )}
        </header>

        {/* Os botoes de estado filtram a lista, nao trocam de painel, portanto
            sao um grupo de botoes premidos e nao um tablist. O ativo leva uma
            barra de 2px na cor do texto: nao mint, que da 2.00:1 e uma barra
            que significa "selecionado" tem de se ver. */}
        <div
          role="group"
          aria-label={translate("conversationsStatusGroup")}
          className="flex items-stretch h-10 px-2 shrink-0 border-b border-[var(--color-border)]"
        >
          {statusOptions.map((option) => {
            const active = status === option.value;
            return (
              <button
                key={option.value || "all"}
                type="button"
                aria-pressed={active}
                onClick={() => chooseStatus(option.value)}
                className="px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-[120ms] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)] ring-inset rounded-sm"
                // A barra e o mint da casa. Ela sozinha da 2.00:1, abaixo dos
                // 3:1 de um indicador, mas nao esta sozinha: o texto do botao
                // ativo salta de secundario para primario, que e uma diferenca
                // de claridade e nao de matiz, e essa passa.
                style={{
                  color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                  boxShadow: active ? "inset 0 -2px 0 var(--color-mint)" : undefined,
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* O que esta ligado no painel, visivel com o painel fechado. Cada chip
            e o botao que o desliga. */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 shrink-0 border-b border-[var(--color-border)]">
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.remove}
                aria-label={translate("conversationsRemoveFilter", { label: chip.label })}
                className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md text-xs font-medium border border-[var(--color-border)] max-w-full transition-colors duration-[120ms] hover:bg-[var(--color-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
                style={{ color: "var(--color-text-primary)" }}
              >
                <span className="truncate">{chip.label}</span>
                <X size={12} className="shrink-0" style={{ color: "var(--color-text-secondary)" }} aria-hidden="true" />
              </button>
            ))}
          </div>
        )}

        {/* O gutter da barra de scroll fica reservado nos dois estados: sem
            ele a barra nasce com a primeira pagina, encolhe as linhas 6px e o
            esqueleto que estava por baixo nao era afinal do tamanho delas.

            O `relative` nao e decoracao: o `sr-only` do Tailwind e
            `position: absolute`, e sem um antepassado posicionado dentro do
            scroller a caixa dele conta para o bloco que contem tudo, que e a
            raiz. Com cinquenta linhas, cada uma com a sua palavra de estado
            escondida, a pagina inteira ganhava 2500px de scroll por baixo de
            conteudo nenhum. Medido: 3480px de altura de documento numa janela
            de 1000. */}
        <div ref={scrollerRef} className="relative flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable]">
          {loading ? (
            <ListSkeleton label={translate("loading")} />
          ) : failed ? (
            // A falha tem precedencia sobre o vazio: uma lista que nao
            // carregou nao pode dizer que nao ha conversas.
            <ColumnNotice icon={AlertCircle} title={translate("conversationsLoadFailed")} action={retry} />
          ) : items.length === 0 ? (
            <ColumnNotice
              icon={Inbox}
              title={translate("conversationsEmpty")}
              hint={translate(anyFilterOn ? "conversationsEmptyFiltered" : "conversationsEmptyHint")}
            />
          ) : (
            <>
              <div className="divide-y divide-[var(--color-border)]">{items.map(row)}</div>
              {/* Duas linhas de esqueleto enquanto a pagina seguinte vem, com a
                  geometria das linhas, para a lista crescer sem saltar. */}
              {loadingMore && (
                <div className="border-t border-[var(--color-border)]">
                  <ListSkeleton label={translate("loading")} rows={SKELETON_ROWS.slice(0, 2)} />
                </div>
              )}
              {hasMore && !loadingMore && <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
            </>
          )}
        </div>
      </section>

      <section className={`flex-1 min-w-0 min-h-0 ${selectedId ? "flex" : "hidden lg:flex"}`}>
        {selectedId ? (
          // A `key` faz o React remontar em vez de reconciliar. Sem ela, a
          // troca de conversa reaproveitava a instancia e o ecra ficava com a
          // conversa anterior inteira por baixo do URL novo ate a leitura
          // seguinte responder. Tambem e ela que garante que uma resposta
          // atrasada da conversa anterior aterra numa instancia desmontada.
          <ConversationDetail
            key={selectedId}
            conversationId={selectedId}
            onLoaded={handleConversationLoaded}
            contactPanelOpen={contactPanelOpen}
            onToggleContactPanel={toggleContactPanel}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <ColumnNotice
              icon={MessagesSquare}
              title={translate("conversationsPickOne")}
              hint={translate("conversationsPickOneHint")}
            />
          </div>
        )}
      </section>

      {/* A terceira coluna so existe com uma conversa aberta e quando o
          operador a quer (ver readContactPanelPreference). Fica depois da
          transcricao no DOM, que e tambem a ordem em que o teclado lhe chega. */}
      {selectedId && contactPanelOpen && (
        <ConversationContactPanel
          key={selectedId}
          conversationId={selectedId}
          conversation={activeConversation}
          loading={!contactAnswered}
        />
      )}
    </div>
  );
};

export default ConversationsWorkspace;
